import { api, API_URL } from './client';

// Fetch all field definitions for the tenant
export const fetchFields = async () => {
  return api.get(`${API_URL}/api/v1/analytics/fields`);
};

// Fetch fields for a specific Salesforce object
export const fetchFieldsByObject = async (objectName) => {
  return api.get(`${API_URL}/api/v1/analytics/fields/${encodeURIComponent(objectName)}`);
};

// Fetch field dependencies analysis
export const fetchDependencies = async () => {
  return api.get(`${API_URL}/api/v1/analytics/dependencies`);
};

// Fetch sync configurations
export const fetchSyncConfigs = async () => {
  return api.get(`${API_URL}/api/v1/analytics/sync/configs`);
};

// Create a new sync configuration
export const createSyncConfig = async (config) => {
  return api.post(`${API_URL}/api/v1/analytics/sync/configs`, config);
};

// Delete a sync configuration
export const deleteSyncConfig = async (configId) => {
  return api.delete(`${API_URL}/api/v1/analytics/sync/configs/${configId}`);
};

// Run an ad-hoc query — returns { columns, rows, row_count, ... }
export const runQuery = async (query, dataSourceId = null) => {
  const body = { query };
  if (dataSourceId) {
    body.data_source_id = dataSourceId;
  }
  return api.post(`${API_URL}/api/v1/analytics/query`, body);
};

// Export query results.
//   format: "csv"  → returns a Blob (text/csv), triggers browser download when passed to downloadBlob()
//           "json" → returns a Blob (application/json), array of row objects
//
// Example:
//   const blob = await exportQuery(sql, 'csv');
//   downloadBlob(blob, 'results.csv');
export const exportQuery = async (query, format = 'csv', dataSourceId = null) => {
  const body = { query };
  if (dataSourceId) body.data_source_id = dataSourceId;
  const resp = await fetch(`${API_URL}/api/v1/analytics/query?format=${format}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ message: resp.statusText }));
    throw new Error(err.message || resp.statusText);
  }
  return resp.blob();
};

// Trigger a browser file download from a Blob.
export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// Trigger a field sync workflow
export const triggerFieldSync = async (objectNames = [], dataSourceId = null) => {
  const body = { object_names: objectNames };
  if (dataSourceId) {
    body.data_source_id = dataSourceId;
  }
  return api.post(`${API_URL}/api/v1/analytics/sync/trigger`, body);
};

// Get sync status (Salesforce connection status and whether data exists)
export const fetchSyncStatus = async () => {
  return api.get(`${API_URL}/api/v1/analytics/sync/status`);
};

// Get workflow status (for polling during sync)
export const fetchWorkflowStatus = async (workflowId) => {
  return api.get(`${API_URL}/api/v1/analytics/sync/workflow/${encodeURIComponent(workflowId)}`);
};

// =============================================================================
// SYNC CONFIGURATION APIs
// =============================================================================

// List all objects with sync status
export const fetchSyncObjects = async (dataSourceId = null) => {
  const query = dataSourceId ? `?data_source_id=${encodeURIComponent(dataSourceId)}` : '';
  return api.get(`${API_URL}/api/v1/analytics/sync/objects${query}`);
};

// List fields for an object with selection status
export const fetchSyncObjectFields = async (objectName, dataSourceId = null) => {
  const query = dataSourceId ? `?data_source_id=${encodeURIComponent(dataSourceId)}` : '';
  return api.get(`${API_URL}/api/v1/analytics/sync/objects/${encodeURIComponent(objectName)}/fields${query}`);
};

// Bulk update sync configurations (add/remove objects)
export const updateSyncObjects = async (add, remove, dataSourceId = null) => {
  const body = { add, remove };
  if (dataSourceId) body.data_source_id = dataSourceId;
  return api.put(`${API_URL}/api/v1/analytics/sync/objects`, body);
};

// Update fields for an object
export const updateSyncObjectFields = async (objectName, action, fields, dataSourceId = null) => {
  const query = dataSourceId ? `?data_source_id=${encodeURIComponent(dataSourceId)}` : '';
  return api.put(`${API_URL}/api/v1/analytics/sync/objects/${encodeURIComponent(objectName)}/fields${query}`, {
    action,
    fields,
  });
};

// Trigger metadata refresh workflow
export const refreshSyncMetadata = async (objectNames = [], dataSourceId = null) => {
  const body = { object_names: objectNames };
  if (dataSourceId) body.data_source_id = dataSourceId;
  return api.post(`${API_URL}/api/v1/analytics/sync/refresh-metadata`, body);
};

// Poll workflow progress
export const getSyncWorkflowProgress = async (workflowId) => {
  return api.get(`${API_URL}/api/v1/analytics/sync/workflow-progress/${encodeURIComponent(workflowId)}`);
};

// =============================================================================
// TENANT SYNC SETTINGS & INCREMENTAL SYNC APIs
// =============================================================================

// Get tenant sync settings (frequency)
export const fetchSyncSettings = async () => {
  return api.get(`${API_URL}/api/v1/analytics/sync/settings`);
};

// Update tenant sync settings
export const updateSyncSettings = async (frequency) => {
  return api.put(`${API_URL}/api/v1/analytics/sync/settings`, {
    default_sync_frequency: frequency,
  });
};

// Trigger manual sync now (all configured objects)
export const triggerSyncNow = async (dataSourceId = null) => {
  const body = dataSourceId ? { data_source_id: dataSourceId } : {};
  return api.post(`${API_URL}/api/v1/analytics/sync/trigger-now`, body);
};

// Get full sync status including active workflow
export const fetchFullSyncStatus = async () => {
  return api.get(`${API_URL}/api/v1/analytics/sync/full-status`);
};

// =============================================================================
// VIEWS APIs
// =============================================================================

// List all views
export const fetchViews = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.category) params.append('category', filters.category);
  if (filters.source) params.append('source', filters.source);
  const query = params.toString() ? `?${params.toString()}` : '';
  return api.get(`${API_URL}/api/v1/analytics/views${query}`);
};

// Get a single view
export const fetchView = async (viewId) => {
  return api.get(`${API_URL}/api/v1/analytics/views/${encodeURIComponent(viewId)}`);
};

// Create a new view
export const createView = async (view) => {
  return api.post(`${API_URL}/api/v1/analytics/views`, view);
};

// Update a view
export const updateView = async (viewId, updates) => {
  return api.put(`${API_URL}/api/v1/analytics/views/${encodeURIComponent(viewId)}`, updates);
};

// Delete a view
export const deleteView = async (viewId) => {
  return api.delete(`${API_URL}/api/v1/analytics/views/${encodeURIComponent(viewId)}`);
};

// Execute a view
export const executeView = async (viewId, params = {}, limit = 1000) => {
  return api.post(`${API_URL}/api/v1/analytics/views/${encodeURIComponent(viewId)}/execute`, {
    params,
    limit,
  });
};

// Validate SOQL syntax
export const validateView = async (soqlContent) => {
  return api.post(`${API_URL}/api/v1/analytics/views/validate`, {
    soql_content: soqlContent,
  });
};

// =============================================================================
// VISUALIZATIONS APIs
// =============================================================================

// List all visualizations
export const fetchVisualizations = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.category) params.append('category', filters.category);
  if (filters.source) params.append('source', filters.source);
  const query = params.toString() ? `?${params.toString()}` : '';
  return api.get(`${API_URL}/api/v1/analytics/visualizations${query}`);
};

// Get a single visualization
export const fetchVisualization = async (vizId) => {
  return api.get(`${API_URL}/api/v1/analytics/visualizations/${encodeURIComponent(vizId)}`);
};

// Create a new visualization
export const createVisualization = async (viz) => {
  return api.post(`${API_URL}/api/v1/analytics/visualizations`, viz);
};

// Update a visualization
export const updateVisualization = async (vizId, updates) => {
  return api.put(`${API_URL}/api/v1/analytics/visualizations/${encodeURIComponent(vizId)}`, updates);
};

// Delete a visualization
export const deleteVisualization = async (vizId) => {
  return api.delete(`${API_URL}/api/v1/analytics/visualizations/${encodeURIComponent(vizId)}`);
};

// Render a visualization (execute views and return JS code + data)
export const renderVisualization = async (vizId, params = {}) => {
  return api.post(`${API_URL}/api/v1/analytics/visualizations/${encodeURIComponent(vizId)}/render`, {
    params,
  });
};

// Clone a visualization
export const cloneVisualization = async (vizId) => {
  return api.post(`${API_URL}/api/v1/analytics/visualizations/${encodeURIComponent(vizId)}/clone`, {});
};

// Export views to JSON
export const exportViews = async (viewIds) => {
  return api.post(`${API_URL}/api/v1/analytics/views/export`, { view_ids: viewIds });
};

// Import views from JSON
export const importViews = async (exportData) => {
  return api.post(`${API_URL}/api/v1/analytics/views/import`, exportData);
};

// Export visualizations to JSON
export const exportVisualizations = async (vizIds, includeViews = true) => {
  return api.post(`${API_URL}/api/v1/analytics/visualizations/export`, {
    visualization_ids: vizIds,
    include_views: includeViews,
  });
};

// Import visualizations from JSON
export const importVisualizations = async (exportData) => {
  return api.post(`${API_URL}/api/v1/analytics/visualizations/import`, exportData);
};

// =============================================================================
// DASHBOARDS APIs
// =============================================================================

// List all dashboards
export const fetchDashboards = async () => {
  return api.get(`${API_URL}/api/v1/analytics/dashboards`);
};

// Get a single dashboard
export const fetchDashboard = async (dashId) => {
  return api.get(`${API_URL}/api/v1/analytics/dashboards/${encodeURIComponent(dashId)}`);
};

// Create a new dashboard
export const createDashboard = async (dashboard) => {
  return api.post(`${API_URL}/api/v1/analytics/dashboards`, dashboard);
};

// Update a dashboard
export const updateDashboard = async (dashId, updates) => {
  return api.put(`${API_URL}/api/v1/analytics/dashboards/${encodeURIComponent(dashId)}`, updates);
};

// Delete a dashboard
export const deleteDashboard = async (dashId) => {
  return api.delete(`${API_URL}/api/v1/analytics/dashboards/${encodeURIComponent(dashId)}`);
};

// Render a dashboard (execute all panels)
export const renderDashboard = async (dashId, filters = {}) => {
  return api.get(`${API_URL}/api/v1/analytics/dashboards/${encodeURIComponent(dashId)}/render`, {
    params: { filters: JSON.stringify(filters) },
  });
};

// =============================================================================
// REFERENCE LIBRARY APIs
// =============================================================================

// Get the full reference catalog
export const fetchReferenceCatalog = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.type) params.append('type', filters.type);
  if (filters.category) params.append('category', filters.category);
  if (filters.tag) params.append('tag', filters.tag);
  if (filters.includeDeprecated) params.append('include_deprecated', 'true');
  const query = params.toString() ? `?${params.toString()}` : '';
  return api.get(`${API_URL}/api/v1/analytics/reference/catalog${query}`);
};

// List reference views
export const fetchReferenceViews = async (category = null) => {
  const query = category ? `?category=${encodeURIComponent(category)}` : '';
  return api.get(`${API_URL}/api/v1/analytics/reference/views${query}`);
};

// Get a single reference view with content
export const fetchReferenceView = async (refId) => {
  return api.get(`${API_URL}/api/v1/analytics/reference/views/${encodeURIComponent(refId)}`);
};

// List reference visualizations
export const fetchReferenceVisualizations = async (category = null) => {
  const query = category ? `?category=${encodeURIComponent(category)}` : '';
  return api.get(`${API_URL}/api/v1/analytics/reference/visualizations${query}`);
};

// Install a reference view
export const installReferenceView = async (refId) => {
  return api.post(`${API_URL}/api/v1/analytics/install/view`, { ref_id: refId });
};

// Install a bundle of reference items
export const installReferenceBundle = async (refId, options = {}) => {
  return api.post(`${API_URL}/api/v1/analytics/install/bundle`, {
    ref_id: refId,
    skip_existing: options.skipExisting || false,
    upgrade_installed: options.upgradeInstalled || false,
  });
};

// Check for updates to installed reference items
export const checkInstalledUpdates = async () => {
  return api.get(`${API_URL}/api/v1/analytics/installed/updates`);
};

// =============================================================================
// VIEW LIFECYCLE APIs (for reference library)
// =============================================================================

// Preview an upgrade (diff + impact analysis)
export const previewViewUpgrade = async (viewId) => {
  return api.get(`${API_URL}/api/v1/analytics/views/${encodeURIComponent(viewId)}/upgrade-preview`);
};

// Upgrade a view to the latest reference version
export const upgradeView = async (viewId, options = {}) => {
  return api.post(`${API_URL}/api/v1/analytics/views/${encodeURIComponent(viewId)}/upgrade`, {
    force: options.force || false,
    acknowledged_breaks: options.acknowledgedBreaks || [],
    reason: options.reason || '',
  });
};

// Reset a view to its originally installed version
export const resetView = async (viewId) => {
  return api.post(`${API_URL}/api/v1/analytics/views/${encodeURIComponent(viewId)}/reset`, {});
};

// Clone a view as a custom copy
export const cloneView = async (viewId) => {
  return api.post(`${API_URL}/api/v1/analytics/views/${encodeURIComponent(viewId)}/clone`, {});
};

// Pin a view to prevent automatic updates
export const pinView = async (viewId, reason = '') => {
  return api.post(`${API_URL}/api/v1/analytics/views/${encodeURIComponent(viewId)}/pin`, { reason });
};

// Unpin a view to allow updates
export const unpinView = async (viewId) => {
  return api.post(`${API_URL}/api/v1/analytics/views/${encodeURIComponent(viewId)}/unpin`, {});
};

// Repair missing tables: enable sync config and trigger workflow for each unsynced table.
// tableNames are raw DuckDB names e.g. ["sf_00D5e__Account", "hs_244999383__contacts"]
export const repairMissingTables = async (tableNames) => {
  return api.post(`${API_URL}/api/v1/analytics/sync/repair-missing-tables`, {
    table_names: tableNames,
  });
};

// =============================================================================
// VIEW VERSION HISTORY APIs
// =============================================================================

// List version snapshots for a view (soql_content excluded — use getViewVersion for content)
export const fetchViewVersions = async (viewId) => {
  return api.get(`${API_URL}/api/v1/analytics/views/${encodeURIComponent(viewId)}/versions`);
};

// Get a single version including full soql_content
export const fetchViewVersion = async (viewId, versionId) => {
  return api.get(`${API_URL}/api/v1/analytics/views/${encodeURIComponent(viewId)}/versions/${encodeURIComponent(versionId)}`);
};

// =============================================================================
// REFERENCE LIBRARY ENABLE/STATUS APIs
// =============================================================================

// Enable reference library (one-click setup)
// Installs all reference views/visualizations and configures sync for required objects
export const enableReferenceLibrary = async () => {
  return api.post(`${API_URL}/api/v1/analytics/reference/enable`, {});
};

// Get reference library status for the tenant
export const fetchReferenceLibraryStatus = async () => {
  return api.get(`${API_URL}/api/v1/analytics/reference/status`);
};

// =============================================================================
// TOPIC DISCOVERY APIs
// =============================================================================

// List all topic configs
export const fetchTopicConfigs = async () => {
  return api.get(`${API_URL}/api/v1/analytics/topics/configs`);
};

// Get a single topic config
export const fetchTopicConfig = async (configId) => {
  return api.get(`${API_URL}/api/v1/analytics/topics/configs/${encodeURIComponent(configId)}`);
};

// Create a new topic config
export const createTopicConfig = async (config) => {
  return api.post(`${API_URL}/api/v1/analytics/topics/configs`, config);
};

// Update a topic config
export const updateTopicConfig = async (configId, updates) => {
  return api.put(`${API_URL}/api/v1/analytics/topics/configs/${encodeURIComponent(configId)}`, updates);
};

// Delete a topic config
export const deleteTopicConfig = async (configId) => {
  return api.delete(`${API_URL}/api/v1/analytics/topics/configs/${encodeURIComponent(configId)}`);
};

// Validate column mappings against synced fields
export const validateTopicColumns = async (configId) => {
  return api.get(`${API_URL}/api/v1/analytics/topics/configs/${encodeURIComponent(configId)}/validate-columns`);
};

// Trigger embedding generation
export const prepareTopicEmbeddings = async (configId) => {
  return api.post(`${API_URL}/api/v1/analytics/topics/configs/${encodeURIComponent(configId)}/embeddings/prepare`);
};

// Get embedding status
export const fetchTopicEmbeddingsStatus = async (configId) => {
  return api.get(`${API_URL}/api/v1/analytics/topics/configs/${encodeURIComponent(configId)}/embeddings/status`);
};

// List sessions for a config
export const fetchTopicSessions = async (configId) => {
  return api.get(`${API_URL}/api/v1/analytics/topics/configs/${encodeURIComponent(configId)}/sessions`);
};

// Create a new session
export const createTopicSession = async (configId, session = {}) => {
  return api.post(`${API_URL}/api/v1/analytics/topics/configs/${encodeURIComponent(configId)}/sessions`, session);
};

// Get a single session
export const fetchTopicSession = async (sessionId) => {
  return api.get(`${API_URL}/api/v1/analytics/topics/sessions/${encodeURIComponent(sessionId)}`);
};

// Delete a session
export const deleteTopicSession = async (sessionId) => {
  return api.delete(`${API_URL}/api/v1/analytics/topics/sessions/${encodeURIComponent(sessionId)}`);
};

// Run clustering on a session
export const clusterTopicSession = async (sessionId, options = {}) => {
  return api.post(`${API_URL}/api/v1/analytics/topics/sessions/${encodeURIComponent(sessionId)}/cluster`, options);
};

// Drill into a cluster
export const drillTopicCluster = async (sessionId, clusterId, options = {}) => {
  return api.post(`${API_URL}/api/v1/analytics/topics/sessions/${encodeURIComponent(sessionId)}/drill`, {
    cluster_id: clusterId,
    ...options,
  });
};

// Merge clusters
export const mergeTopicClusters = async (sessionId, clusterIds, newLabel) => {
  return api.post(`${API_URL}/api/v1/analytics/topics/sessions/${encodeURIComponent(sessionId)}/merge`, {
    cluster_ids: clusterIds,
    new_label: newLabel,
  });
};

// Relabel a cluster
export const relabelTopicCluster = async (sessionId, clusterId, newLabel) => {
  return api.post(`${API_URL}/api/v1/analytics/topics/sessions/${encodeURIComponent(sessionId)}/relabel`, {
    cluster_id: clusterId,
    new_label: newLabel,
  });
};

// Finalize a session into a model
export const finalizeTopicSession = async (sessionId, versionLabel = '') => {
  return api.post(`${API_URL}/api/v1/analytics/topics/sessions/${encodeURIComponent(sessionId)}/finalize`, {
    version_label: versionLabel,
  });
};

// Get cluster samples
export const fetchTopicClusterSamples = async (sessionId, clusterId) => {
  return api.get(`${API_URL}/api/v1/analytics/topics/sessions/${encodeURIComponent(sessionId)}/samples/${encodeURIComponent(clusterId)}`);
};

// Split a cluster in-place (children replace it at the same level)
export const splitTopicCluster = async (sessionId, clusterId, options = {}) => {
  return api.post(`${API_URL}/api/v1/analytics/topics/sessions/${encodeURIComponent(sessionId)}/split`, {
    cluster_id: clusterId,
    ...options,
  });
};

// Drill down: create children at level N+1 (parent cluster persists at level N)
export const drillDownTopicCluster = async (sessionId, clusterId, options = {}) => {
  return api.post(`${API_URL}/api/v1/analytics/topics/sessions/${encodeURIComponent(sessionId)}/drill-down`, {
    cluster_id: clusterId,
    ...options,
  });
};

// List all hierarchy levels for a config
export const listTopicLevels = async (configId) => {
  return api.get(`${API_URL}/api/v1/analytics/topics/configs/${encodeURIComponent(configId)}/levels`);
};

// Rename a level (VP, Director, etc.)
export const renameTopicLevel = async (configId, level, name) => {
  return api.put(`${API_URL}/api/v1/analytics/topics/configs/${encodeURIComponent(configId)}/levels/${level}/name`, { name });
};

// List models for a config
export const fetchTopicModels = async (configId) => {
  return api.get(`${API_URL}/api/v1/analytics/topics/configs/${encodeURIComponent(configId)}/models`);
};

// Get a single model
export const fetchTopicModel = async (modelId) => {
  return api.get(`${API_URL}/api/v1/analytics/topics/models/${encodeURIComponent(modelId)}`);
};

// Activate a model
export const activateTopicModel = async (modelId) => {
  return api.post(`${API_URL}/api/v1/analytics/topics/models/${encodeURIComponent(modelId)}/activate`);
};

// Trigger classification (async - returns immediately)
export const classifyTopicRecords = async (modelId, options = {}) => {
  return api.post(`${API_URL}/api/v1/analytics/topics/models/${encodeURIComponent(modelId)}/classify`, options);
};

// Apply classification and write to database
// By default, runs async and returns workflow ID for polling
// Set async=false to wait for completion (may timeout for large datasets)
export const applyTopicModel = async (modelId, options = {}) => {
  return api.post(`${API_URL}/api/v1/analytics/topics/models/${encodeURIComponent(modelId)}/apply`, {
    write_to_database: true,
    async: options.async !== false, // Default to async=true
  });
};

// Poll for classification progress
export const fetchClassificationProgress = async (modelId) => {
  return api.get(`${API_URL}/api/v1/analytics/topics/models/${encodeURIComponent(modelId)}/classification-status`);
};

export const pushTopicsToCRM = async (modelId, body) => {
  return api.post(`${API_URL}/api/v1/analytics/topics/models/${encodeURIComponent(modelId)}/push-to-crm`, body);
};

// =============================================================================
// DATA OPERATIONS APIs (File Upload & CRUD)
// =============================================================================

// Upload a data file (CSV or Excel)
export const uploadDataFile = async (formData, onUploadProgress) => {
  return api.post(`${API_URL}/api/v1/analytics/data/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    ...(onUploadProgress ? { onUploadProgress } : {}),
  });
};

// Delete an uploaded file record and its MinIO blob
export const deleteUploadedFile = (fileId) =>
  api.delete(`${API_URL}/api/v1/analytics/data/upload/${encodeURIComponent(fileId)}`);

// Get file preview (headers + sample rows)
export const getFilePreview = async (fileId) => {
  return api.get(`${API_URL}/api/v1/analytics/data/preview/${encodeURIComponent(fileId)}`);
};

// =============================================================================
// FIELD MAPPING APIs
// =============================================================================

// Auto-map file columns to CRM fields
export const autoMapFields = async (fileId, objectName, sourceType, dataSourceId) => {
  return api.post(`${API_URL}/api/v1/analytics/data/auto-map`, {
    file_id: fileId,
    object_name: objectName,
    source_type: sourceType,
    data_source_id: dataSourceId,
  });
};

// Create a field mapping
export const createFieldMapping = async (mapping) => {
  return api.post(`${API_URL}/api/v1/analytics/data/mappings`, mapping);
};

// Get a single field mapping
export const getFieldMapping = async (mappingId) => {
  return api.get(`${API_URL}/api/v1/analytics/data/mappings/${encodeURIComponent(mappingId)}`);
};

// List all field mappings for a file
export const listFieldMappings = async (fileId) => {
  return api.get(`${API_URL}/api/v1/analytics/data/mappings?file_id=${encodeURIComponent(fileId)}`);
};

// Update a field mapping
export const updateFieldMapping = async (mappingId, updates) => {
  return api.put(`${API_URL}/api/v1/analytics/data/mappings/${encodeURIComponent(mappingId)}`, updates);
};

// Delete a field mapping
export const deleteFieldMapping = async (mappingId) => {
  return api.delete(`${API_URL}/api/v1/analytics/data/mappings/${encodeURIComponent(mappingId)}`);
};

// =============================================================================
// CRUD OPERATIONS APIs
// =============================================================================

// Trigger INSERT operation
export const insertRecords = async (fileId, targetObject, dataSourceId, apiConfig = {}) => {
  return api.post(`${API_URL}/api/v1/analytics/data/insert`, {
    file_id: fileId,
    target_object: targetObject,
    data_source_id: dataSourceId,
    api_type: apiConfig.apiType,
    processing_mode: apiConfig.processingMode,
    batch_size: apiConfig.batchSize,
  });
};

// Validate data before insert/update operations
export const validateData = async (fileId, targetObject, dataSourceId) => {
  return api.post(`${API_URL}/api/v1/analytics/data/validate`, {
    file_id: fileId,
    target_object: targetObject,
    data_source_id: dataSourceId,
  });
};

// Get operation status
export const getOperationStatus = async (workflowId) => {
  return api.get(`${API_URL}/api/v1/analytics/data/operations/${encodeURIComponent(workflowId)}`);
};

// Download operation results CSV
export const downloadOperationResults = async (runId, type = 'all') => {
  // type can be 'all', 'successes', or 'failures'
  const url = `${API_URL}/api/v1/analytics/data/operations/${encodeURIComponent(runId)}/results/${type}`;

  // Use fetch directly for file download
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'X-Tenant-ID': localStorage.getItem('activeTenantId') || '',
      'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
    }
  });

  if (!response.ok) {
    throw new Error(`Failed to download results: ${response.statusText}`);
  }

  // Get filename from Content-Disposition header
  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = `results-${type}.csv`;
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename=(.+)/);
    if (filenameMatch) {
      filename = filenameMatch[1].replace(/['"]/g, '');
    }
  }

  // Download the file
  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);

  return { success: true, filename };
};

// =============================================================================
// MULTI-OBJECT OPERATIONS APIs
// =============================================================================

// Trigger multi-object INSERT operation
// Objects are processed in dependency order (parent before child)
export const insertMultiObjectRecords = async (objects, dataSourceId) => {
  // objects is an array of { objectName, fileId, dependsOn }
  return api.post(`${API_URL}/api/v1/analytics/data/multi-object-insert`, {
    objects: objects,
    data_source_id: dataSourceId,
  });
};

// =============================================================================
// SPREADSHEET STATE APIs
// =============================================================================

// Fetch the sheet state for a view
export const fetchSheetState = async (viewId) => {
  return api.get(`${API_URL}/api/v1/analytics/views/${encodeURIComponent(viewId)}/sheet-state`);
};

// Save the sheet state for a view
export const saveSheetState = async (viewId, state) => {
  return api.put(`${API_URL}/api/v1/analytics/views/${encodeURIComponent(viewId)}/sheet-state`, state);
};

// Delete (reset) the sheet state for a view
export const deleteSheetState = async (viewId) => {
  return api.delete(`${API_URL}/api/v1/analytics/views/${encodeURIComponent(viewId)}/sheet-state`);
};

// Fetch sync history for a view
export const fetchSyncHistory = async (viewId) => {
  return api.get(`${API_URL}/api/v1/analytics/views/${encodeURIComponent(viewId)}/sync-history`);
};

// Sync spreadsheet edits back to Salesforce
export const syncView = async (viewId, syncRequest) => {
  return api.post(`${API_URL}/api/v1/analytics/views/${encodeURIComponent(viewId)}/sync`, syncRequest);
};

// Revert a previous sync by re-applying old values to Salesforce
export const revertSync = async (viewId, syncId, force = false) => {
  const suffix = force ? '?force=true' : '';
  return api.post(`${API_URL}/api/v1/analytics/views/${encodeURIComponent(viewId)}/sync-history/${encodeURIComponent(syncId)}/revert${suffix}`, {});
};

// Execute a view with spreadsheet-specific parameters (lineage + identities)
export const executeViewForSpreadsheet = async (viewId, { limit = 1000, offset = 0, ...params } = {}) => {
  return api.post(`${API_URL}/api/v1/analytics/views/${encodeURIComponent(viewId)}/execute?include_identities=true`, {
    params,
    limit,
    offset,
  });
};

// Build a WebSocket URL for real-time collaboration on a view.
// Converts the HTTP API_URL to a ws:// or wss:// URL.
// tenantId must be passed by the caller (from Redux state, not localStorage).
export const getWebSocketURL = (viewId, tenantId) => {
  const base = API_URL.replace(/^http/, 'ws');
  return `${base}/api/v1/analytics/views/${encodeURIComponent(viewId)}/ws?tenant_id=${encodeURIComponent(tenantId || '')}`;
};

// Create a new custom field on a Salesforce object via the Metadata API.
// fieldDef: { field_name, label, field_type, length?, required?, picklist_values?, description? }
export const createCustomField = async (objectName, fieldDef) => {
  return api.post(`${API_URL}/api/v1/sfdc/metadata/custom-fields`, {
    object_name: objectName,
    ...fieldDef,
  });
};

// Sync range edits back to the promoted_ranges snapshot in sheet state.
// This does NOT touch Salesforce — only updates the range data for future FrankenQL queries.
export const syncRangeEdits = async (viewId, edits) => {
  return api.post(`${API_URL}/api/v1/analytics/views/${encodeURIComponent(viewId)}/sync-range`, {
    edits,
  });
};

// Promote a named range to a queryable FrankenQL data source.
// The range becomes available as `range_<rangeName>` in SOQL queries.
export const promoteRangeToDataSource = async (viewId, rangeName, headers, rows) => {
  return api.post(`${API_URL}/api/v1/analytics/views/${encodeURIComponent(viewId)}/range-sources`, {
    range_name: rangeName,
    headers,
    rows,
  });
};

// ── Forecast Workbench ───────────────────────────────────────────────────────
// NOTE: Forecast routes are under /api/v1/forecast/... (NOT /api/v1/analytics/forecast/...).
// The gateway registers /forecast as a sibling route block to /analytics (routes.go:266).
// ProxyAnalytics strips /api/v1 prefix then re-adds it, forwarding to analytics service
// at /api/v1/forecast/... which the analytics server handles at server.go:399.
export const listForecastConfigs = async () =>
  api.get(`${API_URL}/api/v1/forecast/configs`);
export const createForecastConfig = async (payload) =>
  api.post(`${API_URL}/api/v1/forecast/configs`, payload);
export const updateForecastConfig = async (id, payload) =>
  api.put(`${API_URL}/api/v1/forecast/configs/${encodeURIComponent(id)}`, payload);
export const deleteForecastConfig = async (id) =>
  api.delete(`${API_URL}/api/v1/forecast/configs/${encodeURIComponent(id)}`);
export const assembleForecastRun = async (configId) =>
  api.post(`${API_URL}/api/v1/forecast/configs/${encodeURIComponent(configId)}/assemble`);
export const trainForecastRun = async (configId, runId) =>
  api.post(`${API_URL}/api/v1/forecast/configs/${encodeURIComponent(configId)}/train`, { run_id: runId });
export const promoteForecastRun = async (configId, runId) =>
  api.post(`${API_URL}/api/v1/forecast/configs/${encodeURIComponent(configId)}/promote`, { run_id: runId });
export const listFeatureRuns = async (configId) =>
  api.get(`${API_URL}/api/v1/forecast/configs/${encodeURIComponent(configId)}/runs`);
export const listEDASuggestions = async (configId) =>
  api.get(`${API_URL}/api/v1/forecast/configs/${encodeURIComponent(configId)}/eda/suggestions`);
export const runQualityEDA = async (configId) =>
  api.post(`${API_URL}/api/v1/forecast/configs/${encodeURIComponent(configId)}/eda/quality`);
export const runCorrelationEDA = async (configId, runId) =>
  api.post(`${API_URL}/api/v1/forecast/configs/${encodeURIComponent(configId)}/eda/correlations`, { run_id: runId });
export const predictOne = async (configId, recordId) =>
  api.post(`${API_URL}/api/v1/forecast/configs/${encodeURIComponent(configId)}/predict`, { record_id: recordId });
export const validateForecastView = async (viewId) =>
  api.post(`${API_URL}/api/v1/forecast/validate-view`, { view_id: viewId });
export const fetchForecastViewPrefilter = async () =>
  api.get(`${API_URL}/api/v1/forecast/view-prefilter`);
export const getForecastFeatures = async (configId) =>
  api.get(`${API_URL}/api/v1/forecast/configs/${encodeURIComponent(configId)}/features`);
export const updateForecastFeatures = async (configId, patch) =>
  api.put(`${API_URL}/api/v1/forecast/configs/${encodeURIComponent(configId)}/features`, patch);
export const updateEDASuggestion = async (configId, sid, patch) =>
  api.put(`${API_URL}/api/v1/forecast/configs/${encodeURIComponent(configId)}/eda/suggestions/${encodeURIComponent(sid)}`, patch);
export const getForecastEDAReport = async (configId, runId) =>
  api.get(`${API_URL}/api/v1/forecast/configs/${encodeURIComponent(configId)}/eda/report` + (runId ? `?run_id=${encodeURIComponent(runId)}` : ''));
export const getForecastEDAPairplotUrl = (configId, runId) =>
  `${API_URL}/api/v1/forecast/configs/${encodeURIComponent(configId)}/eda/pairplot` + (runId ? `?run_id=${encodeURIComponent(runId)}` : '');
export const runCleanse = (configId, runId) =>
  api.post(`${API_URL}/api/v1/forecast/configs/${encodeURIComponent(configId)}/cleanse/run`, { run_id: runId });
export const getCleanseReport = (configId) =>
  api.get(`${API_URL}/api/v1/forecast/configs/${encodeURIComponent(configId)}/cleanse/report`);

// =============================================================================
// RELATIONSHIP APIs (Builder V2)
// =============================================================================

// Fetch lookup relationships for a tenant (optionally filtered by source_object)
export const fetchLookupRelationships = async (sourceObject = null) => {
  const query = sourceObject ? `?source_object=${encodeURIComponent(sourceObject)}` : '';
  return api.get(`${API_URL}/api/v1/analytics/relationships/lookup${query}`);
};

// Fetch child relationships for a tenant (optionally filtered by parent_object)
export const fetchChildRelationships = async (parentObject = null) => {
  const query = parentObject ? `?parent_object=${encodeURIComponent(parentObject)}` : '';
  return api.get(`${API_URL}/api/v1/analytics/relationships/child${query}`);
};

// Create a custom (user-defined) relationship
export const createCustomRelationship = async (relationship) => {
  return api.post(`${API_URL}/api/v1/analytics/relationships/custom`, relationship);
};

// Update a custom relationship by ID
export const updateCustomRelationship = async (id, relationship, options = {}) => {
  return api.put(`${API_URL}/api/v1/analytics/relationships/custom/${encodeURIComponent(id)}`, relationship, options);
};

// Repair saved views after an alias rename (replaces old_alias. with new_alias. in soql_content)
export const repairAliasInViews = async (oldAlias, newAlias) => {
  return api.post(`${API_URL}/api/v1/analytics/views/repair-alias`, { old_alias: oldAlias, new_alias: newAlias });
};

// =============================================================================
// STANDARD RELATIONSHIP SUGGESTIONS APIs
// =============================================================================

// Get standard relationship suggestions for all spiritually paired data sources.
// Returns { pairings: [ { child_ds, parent_ds, suggestions: [...] } ] }
export const getRelationshipSuggestions = () =>
  api.get(`${API_URL}/api/v1/analytics/relationship-suggestions`);

// Bulk-create standard relationships for a specific child/parent DS pair.
// relationships: array of { relationship_name, parent_to_child_name, child_object,
//   child_field, parent_object, parent_field, case_insensitive }
export const bulkCreateRelationships = (childDsId, parentDsId, relationships) =>
  api.post(`${API_URL}/api/v1/analytics/relationships/bulk`, {
    child_data_source_id: childDsId,
    parent_data_source_id: parentDsId,
    relationships,
  });


// =============================================================================
// BYOD (Bring Your Own Data) APIs
// =============================================================================

// List BYOD tables for a file data source
export const listBYODTables = (dsId) =>
  api.get(`${API_URL}/api/v1/analytics/data/byod-tables?data_source_id=${encodeURIComponent(dsId)}`);

// Trigger BYOD write workflow (file → DuckDB table)
export const byodWrite = (body) =>
  api.post(`${API_URL}/api/v1/analytics/data/byod-write`, body);

// Delete a BYOD table
export const deleteBYODTable = (dsId, tableName) =>
  api.delete(`${API_URL}/api/v1/analytics/data/byod-tables/${encodeURIComponent(dsId)}/${encodeURIComponent(tableName)}`);

// Rename a BYOD table
export const renameByodTable = (dsId, tableName, newName) =>
  api.patch(`${API_URL}/api/v1/analytics/data/byod-tables/${encodeURIComponent(dsId)}/${encodeURIComponent(tableName)}`, { new_name: newName });
